import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loading, Avatar, StreakBadge } from "../components/common";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";

export default function Players() {
  const [players, setPlayers] = useState(null);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const { isOwner } = useAuth();
  const navigate = useNavigate();

  const load = async () => setPlayers((await api.get("/players")).data);
  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    await api.post("/players", { name: name.trim() });
    setName("");
    toast.success("Player added");
    load();
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}? Their match history stays but the profile is removed.`))
      return;
    await api.delete(`/players/${p.id}`);
    toast.success("Player deleted");
    load();
  };

  const saveEdit = async (p) => {
    await api.put(`/players/${p.id}`, { name: editName.trim() });
    setEditing(null);
    load();
  };

  if (players === null) return <Loading label="Loading players" />;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2">
          Roster
        </div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none">
          Players
        </h1>
      </div>

      <form onSubmit={add} className="flex gap-2 max-w-md">
        <input
          data-testid="new-player-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New player name"
          className="flex-1 bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
        />
        <button
          data-testid="add-player-btn"
          className="flex items-center gap-1.5 px-4 bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold uppercase rounded-sm transition-colors duration-150"
        >
          <Plus className="w-4 h-4" strokeWidth={3} /> Add
        </button>
      </form>

      {players.length === 0 ? (
        <p className="text-slate-500">No players yet.</p>
      ) : (
        <div className="grid gap-1 md:grid-cols-2 lg:grid-cols-3">
          {players.map((p) => (
            <div
              key={p.id}
              data-testid={`player-card-${p.id}`}
              className="panel rounded-sm p-4 flex items-center gap-3"
            >
              <Avatar name={p.name} size={48} />
              <div className="flex-1 min-w-0">
                {editing === p.id ? (
                  <div className="flex items-center gap-1">
                    <input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 bg-[#1A1A1A] border border-[#262626] rounded-sm px-2 py-1 text-white text-sm"
                    />
                    <button onClick={() => saveEdit(p)} className="text-[#34C759] p-1">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditing(null)} className="text-slate-500 p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => navigate(`/players/${p.id}`)}
                    className="font-display text-xl font-bold text-white truncate block hover:text-[#007AFF] transition-colors"
                  >
                    {p.name}
                  </button>
                )}
                <div className="text-sm text-slate-400 font-semibold">
                  {p.overall.matches_won}-{p.overall.matches_lost} · {p.overall.win_pct}% ·{" "}
                  <StreakBadge streak={p.overall.current_streak} className="text-sm" />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  data-testid={`edit-player-${p.id}`}
                  onClick={() => {
                    setEditing(p.id);
                    setEditName(p.name);
                  }}
                  className="text-slate-500 hover:text-white p-1"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {isOwner && (
                  <button
                    data-testid={`delete-player-${p.id}`}
                    onClick={() => remove(p)}
                    className="text-slate-500 hover:text-[#FF3B30] p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
